import { useCallback } from 'react'

import { useLocale } from 'next-intl'

import { indexes } from '@mab-common/utils'

import useSWRInfinite from 'swr/infinite'

import { elasticClientFetcher } from '~/services/elastic/client'

import type { SearchSuggestion } from './types/search'
import { buildInstaSearchQuery, buildSearchQuery } from './builders'
import {
  processInstaSearchResults,
  processSearchResults,
  processSuggestions,
  rankSuggestions,
} from './processors'

const { RecordTypes } = indexes

export function useSearch(
  pageSize: number,
  recordType: indexes.RecordTypes,
  searchTerm?: string,
  filters?: any,
  queriedId?: string | undefined,
  queriedType?: 'artist' | 'collection' | 'genre' | undefined
) {
  const locale = useLocale()

  const getQuery = useCallback(
    (pageIndex: number, previousPageData: any) => {
      if (previousPageData && !previousPageData.length) {
        return null // reached the end
      }
      const pageFrom = pageIndex * pageSize

      return buildSearchQuery(
        pageFrom,
        pageSize,
        recordType,
        searchTerm,
        filters,
        locale
        // queriedId,
        // queriedType
      )
    },
    [pageSize, recordType, searchTerm, filters, locale]
  )

  const { data, error, size, setSize, isValidating, isLoading } =
    useSWRInfinite(getQuery, elasticClientFetcher, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      // revalidateAll: false, // This prevents re-validating all pages
      // persistSize: true, // This helps maintain pagination state
    })

  const { items, facets } = processSearchResults(data, recordType)

  return {
    data: items,
    error: error,
    size: size,
    setSize,
    isValidating: isValidating,
    isLoading: isLoading,
    facets: facets,
  }
}

export function useInstaSearch(searchTerm?: string) {
  const locale = useLocale()

  const getQuery = (pageIndex: number, previousPageData: any) => {
    if (searchTerm === '' || (searchTerm && searchTerm.length === 1)) return
    if (previousPageData && !previousPageData.length) return null // reached the end
    const pageSize = 20
    const pageFrom = pageIndex * 20
    return buildInstaSearchQuery(pageFrom, pageSize, searchTerm, locale)
  }

  const { data, error, isValidating } = useSWRInfinite(
    getQuery,
    elasticClientFetcher,
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    }
  )

  const mappedData = processInstaSearchResults(data)

  const suggestions: SearchSuggestion[] = []

  if (data?.length && searchTerm && searchTerm.length > 1) {
    ;[
      {
        hits: data[0]?.[0]?.hits?.hits || [],
        type: RecordTypes.ARTIST,
        highlightFields: ['name', 'suggestText'],
        textFormatter: (source: any) => source.name,
      },
      {
        hits: data[0]?.[1]?.hits?.hits || [],
        type: RecordTypes.COLLECTION,
        highlightFields: ['title', 'artist'],
        textFormatter: (source: any) => `${source.title} - ${source.artist}`,
      },
      {
        hits: data[0]?.[2]?.hits?.hits || [],
        type: RecordTypes.ARTWORK,
        highlightFields: ['title', 'artist', 'suggestText'],
        textFormatter: (source: any) => `${source.title} - ${source.artist}`,
      },
      {
        hits: data[0]?.[3]?.hits?.hits || [],
        type: RecordTypes.ARTICLES,
        highlightFields: ['title', 'articleSummary', 'author'],
        textFormatter: (source: any) => source.title,
      },
    ].forEach(config => {
      suggestions.push(
        ...processSuggestions(
          config.hits,
          config.type,
          config.highlightFields,
          config.textFormatter
        )
      )
    })
  }

  const topSuggestions = rankSuggestions(suggestions)

  const artworkArtistCollectionData =
    mappedData?.filter(
      item =>
        item.type === RecordTypes.ARTWORK ||
        item.type === RecordTypes.ARTIST ||
        item.type === RecordTypes.COLLECTION
    ) || []

  const articlesData =
    mappedData?.filter(
      item =>
        item.type === RecordTypes.ARTICLES ||
        item.type === RecordTypes.PODCASTS_AND_VIDEOS
    ) || []

  return {
    data: {
      artworkArtistCollectionData,
      articlesData,
    },
    suggestions: topSuggestions,
    error,
    isValidating: isValidating,
  }
}
